import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "../../lib/context/snackbarContext";
import PolicyIcon from "@mui/icons-material/Policy";
import AppLoader from "../apploader";
import DialogHeader from "../dialog/DialogHeader";
import DialogBody from "../dialog/DialogBody";
import Grid from "@mui/material/Grid2";
import Label from "../label";
import InputField from "../InputField";
import DialogActionButtons from "../dialog/DialogActions";
import {
  Dialog,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Rating,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { fetchWorker } from "../../lib/redux/slices/workerSlice";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useJsApiLoader } from "@react-google-maps/api";
import { setSearchKeys } from "../../lib/redux/slices/searchKeysSlice";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

const libraries = ["places"];

const MobileViewSearchModel = forwardRef(({ selectedService }, ref) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isApply, setIsApply] = useState(true);

  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries,
  });

  useEffect(() => {
    if (
      searchKeys?.workerNameSmall ||
      searchKeys?.workerLocationSmall?.address ||
      searchKeys?.workerRatingSmall > 0 ||
      searchKeys?.workerDateTimeSmall
    ) {
      setIsApply(false);
    } else {
      setIsApply(true);
    }
  }, [
    searchKeys?.workerNameSmall,
    searchKeys?.workerLocationSmall,
    searchKeys?.workerRatingSmall,
    searchKeys?.workerDateTimeSmall,
  ]);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeLocation = (event) => {
    const input = event.target.value;
    dispatch(
      setSearchKeys({
        ...searchKeys,
        workerLocationSmall: {
          address: input,
        },
      })
    );

    if (!input.trim() || !isLoaded) {
      setSuggestions([]);
      setAnchorEl(null);
      return;
    }

    setAnchorEl(event.currentTarget); // Set popper anchor

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSuggestions(predictions || []);
      }
    });
  };

  const handleSuggestionClick = (suggestion) => {
    const placeId = suggestion.place_id;

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    service.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        dispatch(
          setSearchKeys({
            ...searchKeys,
            workerLocationSmall: {
              address: suggestion.description,
              latitude,
              longitude,
            },
          })
        );

        setAnchorEl(null);
        setSuggestions([]);
      } else {
        console.error("Error fetching place details:", status);
      }
    });
  };

  const handleApply = async () => {
    if (isApply === true) return;

    if (selectedService) {
      let lat = searchKeys?.workerLocationSmall?.latitude || "";
      let lng = searchKeys?.workerLocationSmall?.longitude || "";

      const query = {
        serviceId: selectedService,
        lat: lat,
        lng: lng,
        name: searchKeys?.workerNameSmall || "",
        rating: searchKeys?.workerRatingSmall || "",
        date: searchKeys?.workerDateTimeSmall || "",
      };

      dispatch(fetchWorker(query));
      handleClose();
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        scroll="body"
        disableEnforceFocus
      >
        <DialogHeader
          title={"applyFilters"}
          secondaryHeading={"filterDialogSubTitle"}
          onClose={handleClose}
        />
        <DialogBody>
          <Grid container rowSpacing={2}>
            <Grid size={12}>
              <Label
                text={t("searchWorkerByRating")}
                required
                htmlFor="worker-search"
              />
              <Rating
                precision={0.5}
                value={searchKeys?.workerRatingSmall}
                onChange={(event, newValue) => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      workerRatingSmall: newValue,
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={12}>
              <Label
                text={`${t("search")} ${t("worker")}`}
                required
                htmlFor="worker-search"
              />
              <InputField
                id={"worker-search"}
                required
                placeholder={t("enterWorkerName")}
                type={"text"}
                value={searchKeys.workerNameSmall}
                onChange={(e) => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      workerNameSmall: e.target.value,
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={12} position={"relative"}>
              <Label text={t("location")} required htmlFor="location" />
              <InputField
                id={"location"}
                placeholder={`${t("search")} ${t("location")}`}
                value={searchKeys?.workerLocationSmall?.address}
                onChange={handleChangeLocation}
              />
              <Popper
                open={suggestions.length > 0}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1500 }} // Ensures it's above the dialog
              >
                <Paper
                  elevation={3}
                  sx={{ width: anchorEl ? anchorEl.offsetWidth : "auto" }}
                >
                  <List>
                    {suggestions.map((suggestion, index) => (
                      <ListItem
                        button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        <ListItemText primary={suggestion.description} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Popper>
            </Grid>
            <Grid size={12}>
              <Label text={t("dateAndTime")} required htmlFor="location" />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  disablePast
                  ampm={false}
                  format="YYYY/MMM/DD"
                  value={searchKeys?.workerDateTimeSmall}
                  onChange={(newValue) => {
                    dispatch(
                      setSearchKeys({
                        ...searchKeys,
                        workerDateTimeSmall: newValue,
                      })
                    );
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-input": {
                      py: "10.5px",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          isConfirmBtnDisable={isApply}
          onCancel={handleClose}
          confirmText="apply"
          onConfirm={handleApply}
        />
      </BootstrapDialog>
    </>
  );
});

export default MobileViewSearchModel;
